import { useState, useEffect } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/Button';
import Input from '@/components/Input';
import { LuX, LuTriangleAlert, LuPlus } from 'react-icons/lu';
import type { CustomField, CustomFieldType } from '../types';
import { getFieldTypeLabel } from '../utils/fieldTypeHelpers';

interface EditCustomFieldModalProps {
  isOpen: boolean;
  field: CustomField | null;
  onClose: () => void;
  onSubmit: (params: {
    name: string;
    field_type: CustomFieldType;
    is_required: boolean;
    unit_name?: string | null;
    options?: string[];
  }) => void;
  isLoading?: boolean;
}

/**
 * カスタムフィールド編集モーダル
 * field_typeに応じて表示するフィールドを変更
 */
export default function EditCustomFieldModal({
  isOpen,
  field,
  onClose,
  onSubmit,
  isLoading = false,
}: EditCustomFieldModalProps) {
  const [fieldName, setFieldName] = useState('');
  const [fieldType, setFieldType] = useState<CustomFieldType>('short_text');
  const [isRequired, setIsRequired] = useState(false);
  const [unitName, setUnitName] = useState('');
  const [options, setOptions] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  // フィールドデータが変更されたら初期値を設定
  useEffect(() => {
    if (field && isOpen) {
      setFieldName(field.name);
      setFieldType(field.field_type);
      setIsRequired(field.is_required);
      setUnitName(field.unit_name || '');
      setOptions(field.custom_field_options.map((opt) => opt.value));
      setError(null);
    }
  }, [field, isOpen]);

  // モーダルが閉じたらフォームをリセット
  useEffect(() => {
    if (!isOpen) {
      setFieldName('');
      setFieldType('short_text');
      setIsRequired(false);
      setUnitName('');
      setOptions([]);
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = () => {
    // バリデーション
    if (!fieldName.trim()) {
      setError('項目名を入力してください');
      return;
    }

    // 選択肢タイプの場合、選択肢が必要
    if (
      (fieldType === 'single_select' || fieldType === 'multiple_select') &&
      options.length === 0
    ) {
      setError('選択肢を1つ以上入力してください');
      return;
    }

    setError(null);
    onSubmit({
      name: fieldName,
      field_type: fieldType,
      is_required: isRequired,
      unit_name: fieldType === 'number' ? unitName || null : null,
      options:
        fieldType === 'single_select' || fieldType === 'multiple_select' ? options : undefined,
    });
  };

  const addOption = () => {
    setOptions([...options, '']);
  };

  const removeOption = (index: number) => {
    setOptions(options.filter((_, i) => i !== index));
  };

  const updateOption = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  // フィールドタイプに応じた表示判定
  const showUnitName = fieldType === 'number';
  const showOptions = fieldType === 'single_select' || fieldType === 'multiple_select';

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent className="max-w-screen-sm">
        <AlertDialogHeader>
          <AlertDialogTitle>項目を編集</AlertDialogTitle>
          <AlertDialogDescription>カスタムフィールドの項目情報を編集します</AlertDialogDescription>
        </AlertDialogHeader>

        {/* スクロール可能なコンテンツ */}
        <div className="max-h-[520px] space-y-6 overflow-x-hidden overflow-y-auto px-6 py-4">
          {/* 公開中のフォームがある場合の警告 */}
          {field &&
            typeof field.published_forms_count === 'number' &&
            field.published_forms_count > 0 && (
              <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
                <LuTriangleAlert className="mt-0.5 size-4 shrink-0 text-amber-600" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-amber-800">
                    警告：公開中の登録フォームが影響を受けます
                  </p>
                  <p className="mt-1 text-xs text-amber-700">
                    この項目を使用している公開中の登録フォームが
                    <span className="font-bold">{field.published_forms_count}件</span>
                    あります。更新すると、これらのフォームは自動的に下書き状態になります。
                  </p>
                </div>
              </div>
            )}

          {/* 項目名入力フィールド */}
          <div className="flex w-[236px] flex-col gap-2">
            <div className="flex items-center gap-2">
              <label className="text-sm font-bold text-gray-900">項目名</label>
              <span className="h-5 rounded-full bg-red-50 px-3 text-xs leading-5 font-bold text-red-600">
                必須
              </span>
            </div>
            <Input
              type="text"
              value={fieldName}
              onChange={(e) => {
                setFieldName(e.target.value);
                setError(null);
              }}
              placeholder="例：コメント"
              disabled={isLoading}
            />
          </div>

          {/* 情報タイプ表示（編集不可） */}
          <div className="flex w-[236px] flex-col gap-2">
            <div className="flex items-center gap-2">
              <label className="text-sm font-bold text-gray-900">情報タイプ</label>
              <span className="h-5 rounded-full bg-red-50 px-3 text-xs leading-5 font-bold text-red-600">
                必須
              </span>
            </div>
            <div className="flex h-10 items-center rounded border border-gray-300 bg-gray-50 px-3">
              <span className="text-sm text-gray-600">{getFieldTypeLabel(fieldType)}</span>
            </div>
            <p className="text-xs text-gray-600">情報タイプは変更できません</p>
          </div>

          {/* 単位名入力フィールド（数値タイプの場合のみ） */}
          {showUnitName && (
            <div className="flex w-[236px] flex-col gap-2">
              <label className="text-sm font-bold text-gray-900">単位名</label>
              <Input
                type="text"
                value={unitName}
                onChange={(e) => setUnitName(e.target.value)}
                placeholder="例：円"
                disabled={isLoading}
              />
            </div>
          )}

          {/* 選択肢入力フィールド（単一選択・複数選択の場合のみ） */}
          {showOptions && (
            <div className="flex flex-col gap-2">
              <label className="text-sm font-bold text-gray-900">選択肢</label>
              <div className="space-y-2">
                {options.map((option, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      type="text"
                      value={option}
                      onChange={(e) => updateOption(index, e.target.value)}
                      placeholder={`選択肢${index + 1}`}
                      disabled={isLoading}
                      className="flex-1"
                    />
                    <button
                      type="button"
                      onClick={() => removeOption(index)}
                      className="rounded p-1 hover:bg-gray-100"
                      disabled={isLoading}
                    >
                      <LuX size={16} className="text-gray-600" />
                    </button>
                  </div>
                ))}
                {options.length === 0 && (
                  <p className="text-sm text-gray-500">選択肢を追加してください</p>
                )}
              </div>
              <Button
                color="secondary"
                size="sm"
                onClick={addOption}
                disabled={isLoading}
                className="w-[108px]"
              >
                <LuPlus size={16} />
                選択肢を追加
              </Button>
            </div>
          )}

          {/* 必須入力トグル */}
          <div className="flex w-[236px] flex-col gap-2">
            <label className="text-sm font-bold text-gray-900">必須入力にする</label>
            <div className="flex justify-start">
              <button
                type="button"
                onClick={() => setIsRequired(!isRequired)}
                className={`relative h-5 w-10 rounded-full transition-colors ${
                  isRequired ? 'bg-blue-600' : 'bg-gray-300'
                }`}
                disabled={isLoading}
              >
                <span
                  className={`absolute top-[2px] size-4 rounded-full bg-white transition-transform ${
                    isRequired ? 'left-[22px]' : 'left-[2px]'
                  }`}
                />
              </button>
            </div>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <AlertDialogFooter>
          <Button color="secondary" size="md" onClick={onClose} disabled={isLoading}>
            キャンセル
          </Button>
          <Button
            color="primary"
            size="md"
            onClick={handleSubmit}
            disabled={isLoading || !fieldName.trim()}
          >
            {isLoading ? '更新中...' : '保存'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
